import { db } from './db';
import { httpClient } from '@/api/httpClient';
import { createId } from '@/shared/utils/createId';
import { dataUrlToFile } from '@/shared/utils/dataUrlToFile';
import { SYNC_SCHEMA_VERSION } from '@/config/appVersion';
import { buildWellEvidenceFormData } from '@features/wellMonitoring/api/wellMonitoringApi';
import type {
  ConflictRecord,
  DangerousWorkEvidenceUploadPayload,
  DangerousWorkSignatureAttachPayload,
  EvidenceUploadPayload,
  IncidentEvidenceUploadPayload,
  IncidentSignatureAttachPayload,
  MonitoringEvidenceUploadPayload,
  SignatureAttachPayload,
  StationChangeEvidenceUploadPayload,
  SyncQueueItem
} from './types';

type SyncResult = {
  ok: boolean;
  response?: unknown;
  remoteUpdatedAt?: string;
  errorMessage?: string;
};

const getRemoteId = (response: unknown) => {
  if (!response || typeof response !== 'object') {
    return undefined;
  }

  const record = response as Record<string, unknown>;
  const id = record.id;

  if (typeof id === 'string' || typeof id === 'number') {
    return String(id);
  }

  return undefined;
};

export const createQueueItem = <TPayload>(
  input: Omit<
    SyncQueueItem<TPayload>,
    'id' | 'attempts' | 'status' | 'createdAt' | 'updatedAt' | 'schemaVersion'
  >
): SyncQueueItem<TPayload> => {
  const now = new Date().toISOString();

  return {
    ...input,
    id: createId(),
    attempts: 0,
    schemaVersion: SYNC_SCHEMA_VERSION,
    status: 'pending',
    createdAt: now,
    updatedAt: now
  };
};

export const detectConflict = (
  localUpdatedAt: string,
  remoteUpdatedAt?: string
): ConflictRecord | undefined => {
  if (!remoteUpdatedAt) {
    return undefined;
  }

  if (new Date(remoteUpdatedAt).getTime() > new Date(localUpdatedAt).getTime()) {
    return {
      localUpdatedAt,
      remoteUpdatedAt,
      strategy: 'manual-review',
      reason: 'Remote record changed after the local offline edit.'
    };
  }

  return undefined;
};

export class SyncEngine {
  private running = false;
  private paused = false;

  /**
   * Pauses queue processing (phase C.5). The version/maintenance interceptor
   * pauses the queue while the backend reports a force-update or a maintenance
   * window so the client stops pushing writes that would fail or be rejected.
   * Enqueueing is unaffected — operations still accumulate locally.
   */
  pause() {
    this.paused = true;
  }

  /** Resumes queue processing after the blocking condition clears. */
  resume() {
    this.paused = false;
  }

  get isPaused() {
    return this.paused;
  }

  async enqueue<TPayload>(
    item: Omit<
      SyncQueueItem<TPayload>,
      'id' | 'attempts' | 'status' | 'createdAt' | 'updatedAt' | 'schemaVersion'
    >
  ) {
    const queueItem = createQueueItem(item);
    await db.syncQueue.add(queueItem);
    return queueItem;
  }

  async processQueue() {
    if (this.running || this.paused || !navigator.onLine) {
      return;
    }

    this.running = true;

    try {
      const pendingItems = await db.syncQueue
        .where('status')
        .anyOf(['pending', 'error'])
        .sortBy('createdAt');

      for (const item of pendingItems) {
        await this.processItem(item);
      }
    } finally {
      this.running = false;
    }
  }

  private async processItem(item: SyncQueueItem) {
    const now = new Date().toISOString();
    await db.syncQueue.update(item.id, {
      status: 'syncing',
      attempts: item.attempts + 1,
      lastAttemptAt: now,
      updatedAt: now
    });

    const result = await this.send(item);
    const conflict = detectConflict(item.updatedAt, result.remoteUpdatedAt);

    if (result.ok && !conflict) {
      await this.markLocalRecordSynced(item, result.response);
      await db.syncQueue.update(item.id, {
        status: 'synced',
        updatedAt: new Date().toISOString(),
        errorMessage: undefined
      });
      return;
    }

    await db.syncQueue.update(item.id, {
      status: 'error',
      conflict,
      errorMessage: conflict?.reason ?? result.errorMessage ?? 'Sync failed',
      updatedAt: new Date().toISOString()
    });
  }

  private async send(item: SyncQueueItem): Promise<SyncResult> {
    try {
      if (item.operation === 'signature.attach') {
        return await this.sendSignatureAttach(item as SyncQueueItem<SignatureAttachPayload>);
      }

      if (item.operation === 'fuelReceptionEvidence.upload') {
        return await this.sendEvidenceUpload(item as SyncQueueItem<EvidenceUploadPayload>);
      }

      if (item.operation === 'monitoring.inspectionEvidence.upload') {
        return await this.sendMonitoringEvidenceUpload(
          item as SyncQueueItem<MonitoringEvidenceUploadPayload>
        );
      }

      if (item.operation === 'incidentSignature.attach') {
        return await this.sendIncidentSignatureAttach(
          item as SyncQueueItem<IncidentSignatureAttachPayload>
        );
      }

      if (item.operation === 'incidentEvidence.upload') {
        return await this.sendIncidentEvidenceUpload(
          item as SyncQueueItem<IncidentEvidenceUploadPayload>
        );
      }

      if (item.operation === 'dangerousWorkSignature.attach') {
        return await this.sendDangerousWorkSignatureAttach(
          item as SyncQueueItem<DangerousWorkSignatureAttachPayload>
        );
      }

      if (item.operation === 'dangerousWorkEvidence.upload') {
        return await this.sendDangerousWorkEvidenceUpload(
          item as SyncQueueItem<DangerousWorkEvidenceUploadPayload>
        );
      }

      if (item.operation === 'stationChangeEvidence.upload') {
        return await this.sendStationChangeEvidenceUpload(
          item as SyncQueueItem<StationChangeEvidenceUploadPayload>
        );
      }

      const response = await httpClient<unknown>(item.endpoint, {
        method: item.method,
        body: JSON.stringify(item.payload)
      });

      return {
        ok: true,
        response
      };
    } catch (error) {
      return {
        ok: false,
        errorMessage: error instanceof Error ? error.message : 'Network error'
      };
    }
  }

  /**
   * Replays a deferred signature upload as a multipart PATCH. The parent
   * `fuelReception.create` runs first (it is enqueued earlier), so by the time
   * this item is processed the draft carries the server id. If the create has
   * not synced yet — e.g. it failed and is still retrying — this stays in the
   * queue as an error and retries on the next pass instead of losing the file.
   */
  private async sendSignatureAttach(
    item: SyncQueueItem<SignatureAttachPayload>
  ): Promise<SyncResult> {
    const { fuelReceptionDraftId, dataUrl, filename } = item.payload;
    const draft = await db.fuelReceptionDrafts.get(fuelReceptionDraftId);
    const remoteId = draft?.remoteId;

    if (!remoteId) {
      return {
        ok: false,
        errorMessage: 'Awaiting fuel reception create before attaching signature'
      };
    }

    const formData = new FormData();
    formData.append('operator_signature', await dataUrlToFile(dataUrl, filename));
    const response = await httpClient<unknown>(`/api/v1/fuel-receptions/${remoteId}/`, {
      method: 'PATCH',
      body: formData
    });

    return {
      ok: true,
      response
    };
  }

  /**
   * Replays a deferred evidence photo as a multipart POST to `/evidence/`,
   * mirroring the online path in `useFuelReception`. Like the signature, it waits
   * on the parent `fuelReception.create` to resolve the `related_entity_id`, and
   * retries on a later pass if the create has not synced yet.
   */
  private async sendEvidenceUpload(
    item: SyncQueueItem<EvidenceUploadPayload>
  ): Promise<SyncResult> {
    const { fuelReceptionDraftId, station, dataUrl, filename, description, capturedAt } =
      item.payload;
    const draft = await db.fuelReceptionDrafts.get(fuelReceptionDraftId);
    const remoteId = draft?.remoteId;

    if (!remoteId) {
      return {
        ok: false,
        errorMessage: 'Awaiting fuel reception create before uploading evidence'
      };
    }

    const formData = new FormData();
    formData.append('station', String(station));
    formData.append('related_entity_type', 'FuelReceptionLog');
    formData.append('related_entity_id', remoteId);
    formData.append('file', await dataUrlToFile(dataUrl, filename));
    formData.append('metadata', JSON.stringify({ description, captured_at: capturedAt }));

    const response = await httpClient<unknown>('/api/v1/evidence/', {
      method: 'POST',
      body: formData
    });

    return {
      ok: true,
      response
    };
  }

  /**
   * Replays a deferred well photo as a multipart POST to `/evidence/`, mirroring
   * the online path in `InspectionWizard`. Waits on the parent
   * `monitoring.inspection.create` to resolve the `related_entity_id`, and retries
   * on a later pass if the create has not synced yet.
   */
  private async sendMonitoringEvidenceUpload(
    item: SyncQueueItem<MonitoringEvidenceUploadPayload>
  ): Promise<SyncResult> {
    const { inspectionDraftId, station, well, step, dataUrl, filename, capturedAt, geolocation } =
      item.payload;
    const draft = await db.wellInspectionDrafts.get(inspectionDraftId);
    const remoteId = draft?.remoteId;

    if (!remoteId) {
      return {
        ok: false,
        errorMessage: 'Awaiting monitoring inspection create before uploading evidence'
      };
    }

    const response = await httpClient<unknown>('/api/v1/evidence/', {
      method: 'POST',
      body: buildWellEvidenceFormData({
        station,
        inspectionId: remoteId,
        well,
        step,
        file: await dataUrlToFile(dataUrl, filename),
        capturedAt,
        geolocation
      })
    });

    return {
      ok: true,
      response
    };
  }

  /**
   * Replays a deferred incident signature as a multipart PATCH. Waits on the
   * parent `incident.create` to resolve the server id, retrying on a later pass
   * if the create has not synced yet.
   */
  private async sendIncidentSignatureAttach(
    item: SyncQueueItem<IncidentSignatureAttachPayload>
  ): Promise<SyncResult> {
    const { incidentDraftId, dataUrl, filename } = item.payload;
    const draft = await db.incidentDrafts.get(incidentDraftId);
    const remoteId = draft?.remoteId;

    if (!remoteId) {
      return { ok: false, errorMessage: 'Awaiting incident create before attaching signature' };
    }

    const formData = new FormData();
    formData.append('operator_signature', await dataUrlToFile(dataUrl, filename));
    const response = await httpClient<unknown>(`/api/v1/incidents/${remoteId}/`, {
      method: 'PATCH',
      body: formData
    });

    return { ok: true, response };
  }

  /**
   * Replays a deferred incident photo as a multipart POST to `/evidence/`.
   * Waits on the parent `incident.create` to resolve the `related_entity_id`,
   * retrying on a later pass if the create has not synced yet.
   */
  private async sendIncidentEvidenceUpload(
    item: SyncQueueItem<IncidentEvidenceUploadPayload>
  ): Promise<SyncResult> {
    const { incidentDraftId, station, dataUrl, filename, description, capturedAt } = item.payload;
    const draft = await db.incidentDrafts.get(incidentDraftId);
    const remoteId = draft?.remoteId;

    if (!remoteId) {
      return { ok: false, errorMessage: 'Awaiting incident create before uploading evidence' };
    }

    const formData = new FormData();
    formData.append('station', String(station));
    formData.append('related_entity_type', 'IncidentLog');
    formData.append('related_entity_id', remoteId);
    formData.append('file', await dataUrlToFile(dataUrl, filename));
    formData.append('metadata', JSON.stringify({ description, captured_at: capturedAt }));

    const response = await httpClient<unknown>('/api/v1/evidence/', {
      method: 'POST',
      body: formData
    });

    return { ok: true, response };
  }

  /**
   * Replays a deferred hazardous-work signature as a multipart PATCH. Waits on
   * the parent `dangerousWork.create` to resolve the server id, retrying on a
   * later pass if the create has not synced yet.
   */
  private async sendDangerousWorkSignatureAttach(
    item: SyncQueueItem<DangerousWorkSignatureAttachPayload>
  ): Promise<SyncResult> {
    const { dangerousWorkDraftId, dataUrl, filename } = item.payload;
    const draft = await db.dangerousWorkDrafts.get(dangerousWorkDraftId);
    const remoteId = draft?.remoteId;

    if (!remoteId) {
      return {
        ok: false,
        errorMessage: 'Awaiting dangerous work create before attaching signature'
      };
    }

    const formData = new FormData();
    formData.append('operator_signature', await dataUrlToFile(dataUrl, filename));
    const response = await httpClient<unknown>(`/api/v1/hazardous-work/${remoteId}/`, {
      method: 'PATCH',
      body: formData
    });

    return { ok: true, response };
  }

  /**
   * Replays a deferred hazardous-work photo as a multipart POST to `/evidence/`.
   * Waits on the parent `dangerousWork.create` to resolve the
   * `related_entity_id`, retrying on a later pass if the create has not synced yet.
   */
  private async sendDangerousWorkEvidenceUpload(
    item: SyncQueueItem<DangerousWorkEvidenceUploadPayload>
  ): Promise<SyncResult> {
    const { dangerousWorkDraftId, station, dataUrl, filename, description, capturedAt } =
      item.payload;
    const draft = await db.dangerousWorkDrafts.get(dangerousWorkDraftId);
    const remoteId = draft?.remoteId;

    if (!remoteId) {
      return {
        ok: false,
        errorMessage: 'Awaiting dangerous work create before uploading evidence'
      };
    }

    const formData = new FormData();
    formData.append('station', String(station));
    formData.append('related_entity_type', 'HazardousWorkLog');
    formData.append('related_entity_id', remoteId);
    formData.append('file', await dataUrlToFile(dataUrl, filename));
    formData.append('metadata', JSON.stringify({ description, captured_at: capturedAt }));

    const response = await httpClient<unknown>('/api/v1/evidence/', {
      method: 'POST',
      body: formData
    });

    return { ok: true, response };
  }

  private async sendStationChangeEvidenceUpload(
    item: SyncQueueItem<StationChangeEvidenceUploadPayload>
  ): Promise<SyncResult> {
    const { stationChangeDraftId, station, dataUrl, filename, category, description, capturedAt } =
      item.payload;
    const draft = await db.stationChangeDrafts.get(stationChangeDraftId);
    const remoteId = draft?.remoteId;

    if (!remoteId) {
      return {
        ok: false,
        errorMessage: 'Awaiting station change create before uploading evidence'
      };
    }

    const formData = new FormData();
    formData.append('station', String(station));
    formData.append('related_entity_type', 'StationChangeLog');
    formData.append('related_entity_id', remoteId);
    formData.append('file', await dataUrlToFile(dataUrl, filename));
    formData.append('metadata', JSON.stringify({ category, description, captured_at: capturedAt }));

    const response = await httpClient<unknown>('/api/v1/evidence/', {
      method: 'POST',
      body: formData
    });

    return { ok: true, response };
  }

  private async markLocalRecordSynced(item: SyncQueueItem, response: unknown) {
    const now = new Date().toISOString();

    switch (item.operation) {
      case 'fuelReception.create':
      case 'fuelReception.patch': {
        await db.fuelReceptionDrafts.update(item.entityId, {
          remoteId: getRemoteId(response),
          syncStatus: 'synced',
          updatedAt: now
        });
        break;
      }
      case 'monitoring.inspection.create': {
        await db.wellInspectionDrafts.update(item.entityId, {
          remoteId: getRemoteId(response),
          syncStatus: 'synced',
          updatedAt: now
        });
        break;
      }
      case 'maintenance.create':
      case 'maintenance.patch': {
        await db.maintenanceDrafts.update(item.entityId, {
          remoteId: getRemoteId(response),
          syncStatus: 'synced',
          updatedAt: now
        });
        break;
      }
      case 'maintenanceEvidence.upload': {
        await db.maintenanceEvidenceDrafts.update(item.entityId, {
          remoteId: getRemoteId(response),
          syncStatus: 'synced'
        });
        break;
      }
      case 'incident.create': {
        await db.incidentDrafts.update(item.entityId, {
          remoteId: getRemoteId(response),
          syncStatus: 'synced',
          updatedAt: now
        });
        break;
      }
      case 'incidentEvidence.upload': {
        await db.incidentEvidenceDrafts.update(item.entityId, {
          remoteId: getRemoteId(response),
          syncStatus: 'synced'
        });
        break;
      }
      case 'dangerousWork.create': {
        await db.dangerousWorkDrafts.update(item.entityId, {
          remoteId: getRemoteId(response),
          syncStatus: 'synced',
          updatedAt: now
        });
        break;
      }
      case 'dangerousWorkEvidence.upload': {
        await db.dangerousWorkEvidenceDrafts.update(item.entityId, {
          remoteId: getRemoteId(response),
          syncStatus: 'synced'
        });
        break;
      }
      case 'stationChange.create':
      case 'stationChange.patch': {
        await db.stationChangeDrafts.update(item.entityId, {
          remoteId: getRemoteId(response),
          syncStatus: 'synced',
          updatedAt: now
        });
        break;
      }
      case 'stationChangeEvidence.upload': {
        await db.stationChangeEvidenceDrafts.update(item.entityId, {
          remoteId: getRemoteId(response),
          syncStatus: 'synced'
        });
        break;
      }
      case 'monitoring.inspectionEvidence.upload': {
        await db.wellInspectionEvidenceDrafts.update(item.entityId, {
          remoteId: getRemoteId(response),
          syncStatus: 'synced'
        });
        break;
      }
      case 'fuelReceptionEvidence.upload':
      case 'evidence.upload': {
        await db.evidences.update(item.entityId, {
          syncStatus: 'synced'
        });
        await db.fuelReceptionEvidenceDrafts.update(item.entityId, {
          remoteId: getRemoteId(response),
          syncStatus: 'synced'
        });
        break;
      }
      case 'signature.attach': {
        await db.signatures.update(item.entityId, {
          syncStatus: 'synced'
        });
        break;
      }
      default:
        break;
    }
  }
}

export const syncEngine = new SyncEngine();
